import {Platform} from 'react-native';

// Emerald Mobility design tokens — see DESIGN.md at the repo root.
// Where the YAML front matter and the prose disagree, the prose role
// assignments win (e.g. primary #0F4C3A, surface #F8FAF9).

export const colors = {
  // Brand
  primary: '#0F4C3A', // CTAs, brand headers, active nav, authoritative type
  primaryPressed: '#0B3B2D',
  primaryDeep: '#003426',
  onPrimary: '#FFFFFF',
  onPrimaryMuted: '#82BBA4', // secondary text on primary surfaces
  secondary: '#10B981', // live signals: routes, progress, "available" tags
  tertiary: '#34D399', // halos, active radio, pressed highlights
  primaryFixed: '#B4EFD6', // soft mint badge backdrop
  mintTint: 'rgba(16, 185, 129, 0.04)', // selected card fill
  mintSoft: 'rgba(16, 185, 129, 0.12)', // chips / icon backdrops
  halo: 'rgba(52, 211, 153, 0.30)', // pulsing beacon ring

  // Auth screens (Stitch "Pompi - Login & Onboarding")
  brandDeep: '#064E3B', // wordmark, CTA gradient start
  brandMid: '#065F46', // CTA gradient middle, eyebrow pill text
  brandBright: '#059669', // CTA gradient end, CTA glow
  brandLink: '#047857', // inline links, ® badge
  mintWash: '#ECFDF5', // eyebrow pill fill
  mintLine: '#D1FAE5', // eyebrow pill border, ® badge fill
  ink: '#111827', // Apple button
  inkPressed: '#000000',
  textStrong: '#1E293B', // labels on light auth buttons
  textEmphasis: '#334155', // bold runs inside muted fine print

  // Neutrals
  background: '#F8FAF9', // app canvas
  surface: '#FFFFFF', // cards & sheets
  surfaceMuted: '#F1F5F9', // inactive chips, input fills
  text: '#0F172A',
  textMuted: '#64748B',
  textSubtle: '#94A3B8', // inactive nav, disabled
  border: '#E2E8F0',
  borderSubtle: '#F1F5F9',
  borderTint: 'rgba(15, 76, 58, 0.08)',
  overlay: 'rgba(15, 23, 42, 0.45)', // modal scrim
  glass: 'rgba(255, 255, 255, 0.94)', // floating pills over the map

  // Feedback
  error: '#BA1A1A',
  errorPressed: '#93000A',
  errorTint: '#FFF8F7', // faint fill behind invalid inputs
  errorContainer: '#FFDAD6',
  onErrorContainer: '#93000A',
  warning: '#F59E0B',
  warningContainer: '#FEF3C7',
  onWarningContainer: '#92400E', // text on warningContainer (AA contrast)
};

export const fonts = {
  regular: 'PlusJakartaSans-Regular',
  medium: 'PlusJakartaSans-Medium',
  semiBold: 'PlusJakartaSans-SemiBold',
  bold: 'PlusJakartaSans-Bold',
};

// Weight is carried by the font file, so never pair these with fontWeight
// (Android would synthesize a faux-bold on top).
export const typography = {
  displayLg: {fontFamily: fonts.bold, fontSize: 36, lineHeight: 44, letterSpacing: -0.72},
  displayMobile: {fontFamily: fonts.bold, fontSize: 28, lineHeight: 34, letterSpacing: -0.28},
  headlineLg: {fontFamily: fonts.semiBold, fontSize: 24, lineHeight: 30, letterSpacing: -0.36},
  headlineSm: {fontFamily: fonts.semiBold, fontSize: 18, lineHeight: 24, letterSpacing: -0.09},
  bodyLg: {fontFamily: fonts.regular, fontSize: 16, lineHeight: 24},
  bodyMd: {fontFamily: fonts.regular, fontSize: 14, lineHeight: 20},
  bodySm: {fontFamily: fonts.regular, fontSize: 12, lineHeight: 16},
  labelLg: {fontFamily: fonts.semiBold, fontSize: 14, lineHeight: 20, letterSpacing: 0.14},
  labelMd: {fontFamily: fonts.semiBold, fontSize: 12, lineHeight: 16, letterSpacing: 0.24},
  labelSm: {fontFamily: fonts.semiBold, fontSize: 11, lineHeight: 14, letterSpacing: 0.44},
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 20,
  xl: 28,
  xxl: 40,
  margin: 16, // screen side margin (margin-mobile)
  gutter: 12,
};

export const radii = {
  badge: 8, // price bubbles, small tags
  sm: 12, // inner elements, thumbnails
  card: 20, // station quick-cards, list cards
  sheet: 28, // bottom sheets, modals
  pill: 9999, // buttons, inputs, chips, search
};

// Forest-tinted ambient shadows. Android only honours shadowColor with
// elevation on API 28+, which covers our minSdk path well enough.
//
// RN 0.77 Android bug: the elevation outline (CompositeBackgroundDrawable
// .getOutline) treats borderRadius dp values as px, so shadows under
// non-pill rounded shapes get near-square corners. Level 1 is barely
// visible by design, so on Android it carries no elevation and cards lean
// on their border instead. Pills are unaffected (radius clamps to h/2).
export const shadows = {
  level1: {
    shadowColor: colors.primary,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.06,
    shadowRadius: 10,
    elevation: Platform.OS === 'android' ? 0 : 2,
  },
  level2: {
    shadowColor: colors.primary,
    shadowOffset: {width: 0, height: 8},
    shadowOpacity: 0.1,
    shadowRadius: 15,
    elevation: 6,
  },
  level3: {
    shadowColor: colors.primary,
    shadowOffset: {width: 0, height: 12},
    shadowOpacity: 0.22,
    shadowRadius: 16,
    elevation: 10,
  },
};

// Level-1 card surface: white, feather border, ambient shadow.
export const card = {
  backgroundColor: colors.surface,
  borderRadius: radii.card,
  borderWidth: 1,
  // Without elevation (see shadows note) Android needs the firmer border.
  borderColor: Platform.OS === 'android' ? colors.border : colors.borderSubtle,
  ...shadows.level1,
};
